"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import { AnimatedBuildingBackground } from "@/components/ui/AnimatedBuildingBackground";
import { userApi } from "@/lib/api/user";

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await userApi.register(formData);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative z-0">
        <AnimatedBuildingBackground />
        <div className="absolute top-8 left-8">
          <Link href="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-full border border-primary/20 transition-all backdrop-blur-sm">
            &larr; Back to Home
          </Link>
        </div>
        <Card 
          className="w-full max-w-lg p-4 sm:p-8 backdrop-blur-xl relative z-10"
          style={{
            borderColor: "rgba(56, 229, 182, 0.35)",
            background: "rgba(6, 21, 18, 0.75)",
            boxShadow: "0 0 0 1px rgba(56,229,182,0.06), 0 30px 60px -20px rgba(0,0,0,0.7)",
          }}
        >
          <CardHeader>
            <CardTitle className="text-2xl text-center">Check Your Email</CardTitle>
            <CardDescription className="text-center text-lg mt-2">
              We&apos;ve sent a verification link to <span className="font-semibold">{formData.email}</span>.
              Please verify your email to continue.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center bg-transparent border-none pt-4">
            <Link href="/login">
              <Button variant="outline" className="w-full">Back to Login</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative z-0">
      <AnimatedBuildingBackground />
      <div className="absolute top-8 left-8">
        <Link href="/" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-full border border-primary/20 transition-all backdrop-blur-sm">
          &larr; Back to Home
        </Link>
      </div>
      <Card 
        className="w-full max-w-lg p-4 sm:p-8 backdrop-blur-xl relative z-10 bg-card/90 border-primary/30 shadow-2xl"
      >
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl font-bold tracking-tight text-center">Create an account</CardTitle>
          <CardDescription className="text-center">
            Enter your information to get started with realpilot
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 text-red-500 rounded-md text-sm">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label htmlFor="first_name" className="text-base text-muted-foreground">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  placeholder="John"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="last_name" className="text-base text-muted-foreground">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  placeholder="Doe"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="email" className="text-base text-muted-foreground">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john.doe@example.com"
                required
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="password" className="text-base text-muted-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full text-base font-semibold" disabled={loading}>
              {loading ? "Creating account..." : "Sign Up"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center bg-transparent border-none pb-2 pt-4">
          <p className="text-base text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:underline">
              Log in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
